import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowUpRight,
  Building2,
  CheckCircle2,
  FileText,
  Users,
  TrendingUp,
  TrendingDown,
  AlertCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { MonthlyTrendsChart } from '@/components/dashboard/monthly-trends-chart';
import { DepartmentChart } from '@/components/dashboard/department-chart';
import { PropertyTypeChart } from '@/components/dashboard/property-type-chart';
import { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDashboardAnalytics } from '@/hooks/queries/dashboard';

interface KpiCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  description: string;
  trend?: number | null;
  isLoading?: boolean;
}

function KpiCard({
  title,
  value,
  icon: Icon,
  description,
  trend,
  isLoading,
}: KpiCardProps) {
  if (isLoading) {
    return (
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 px-4 sm:px-6">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-5 rounded" />
        </CardHeader>
        <CardContent className="px-4 sm:px-6 pb-4">
          <Skeleton className="h-8 w-16 mb-2" />
          <Skeleton className="h-3 w-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border/50">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 px-4 sm:px-6">
        <CardTitle className="text-sm font-medium text-card-foreground">
          {title}
        </CardTitle>
        <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
      </CardHeader>
      <CardContent className="px-4 sm:px-6 pb-4">
        <div className="text-xl sm:text-2xl font-bold font-headline text-primary">
          {value}
        </div>
        <div className="flex items-center gap-2 mt-1">
          {trend !== undefined && trend !== null && (
            <span
              className={`text-xs font-medium flex items-center ${
                trend > 0
                  ? 'text-green-600'
                  : trend < 0
                    ? 'text-red-600'
                    : 'text-muted-foreground'
              }`}
            >
              {trend > 0 ? (
                <TrendingUp className="h-3 w-3 mr-1" />
              ) : trend < 0 ? (
                <TrendingDown className="h-3 w-3 mr-1" />
              ) : null}
              {trend > 0 ? '+' : ''}
              {trend}%
            </span>
          )}
          <p className="text-xs sm:text-sm text-muted-foreground">
            {description}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { data: analytics, isLoading, error } = useDashboardAnalytics();

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <div className="text-center">
          <p className="text-sm text-muted-foreground mt-1">
            {error instanceof Error
              ? error.message
              : 'Failed to load dashboard data'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6 lg:gap-8">
      <div className="space-y-1">
        <h1 className="text-2xl sm:text-3xl font-bold font-headline text-primary">
          {t('dashboard.superAdminTitle')}
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          {t('dashboard.welcome')}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-3 sm:gap-4 grid-cols-1 xs:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title={t('dashboard.stats.totalProperties')}
          value={analytics?.overview.total_properties ?? 0}
          icon={Building2}
          description={`${analytics?.overview.active_properties ?? 0} active`}
          isLoading={isLoading}
        />
        <KpiCard
          title={t('dashboard.stats.avgScore')}
          value={
            analytics?.overview.average_score
              ? `${analytics.overview.average_score}%`
              : 'N/A'
          }
          icon={CheckCircle2}
          description={`${analytics?.overview.completed_assessments ?? 0} completed`}
          isLoading={isLoading}
        />
        <KpiCard
          title={t('dashboard.stats.auditsThisMonth')}
          value={analytics?.overview.assessments_this_month ?? 0}
          icon={FileText}
          description={`${analytics?.overview.total_assessments ?? 0} total`}
          isLoading={isLoading}
        />
        <KpiCard
          title={t('dashboard.stats.totalUsers')}
          value={analytics?.overview.total_users ?? 0}
          icon={Users}
          description={t('dashboard.stats.activeUsers')}
          isLoading={isLoading}
        />
      </div>

      {/* Action Items Alert */}
      {analytics?.overview && analytics.overview.overdue_assessments > 0 && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="flex items-center gap-3 p-4">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">
                {analytics.overview.overdue_assessments} overdue assessments
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {analytics.overview.upcoming_due_this_week} due this week
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row 1: Monthly Trends & Department Performance */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 xl:grid-cols-2">
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardHeader className="px-4 sm:px-6">
            <CardTitle className="text-lg sm:text-xl font-headline text-primary">
              {t('dashboard.charts.monthlyTrends')}
            </CardTitle>
            <CardDescription className="text-sm">
              {t('dashboard.charts.last6Months')}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-2 sm:px-4 pb-4">
            {isLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : analytics?.monthly_trends ? (
              <div className="h-[300px]">
                <MonthlyTrendsChart data={analytics.monthly_trends} />
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                {t('dashboard.noData')}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardHeader className="px-4 sm:px-6">
            <CardTitle className="text-lg sm:text-xl font-headline text-primary">
              {t('dashboard.charts.departmentPerformance')}
            </CardTitle>
            <CardDescription className="text-sm">
              {t('dashboard.charts.complianceByDepartment')}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-2 sm:px-4 pb-4">
            {isLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : analytics?.department_performance &&
              analytics.department_performance.length > 0 ? (
              <div className="h-[300px]">
                <DepartmentChart data={analytics.department_performance} />
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                {t('dashboard.noData')}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Property Type Breakdown & Top Performers */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 xl:grid-cols-7">
        <Card className="xl:col-span-3 bg-card/50 backdrop-blur-sm border-border/50">
          <CardHeader className="px-4 sm:px-6">
            <CardTitle className="text-lg sm:text-xl font-headline text-primary">
              {t('dashboard.charts.propertyTypes')}
            </CardTitle>
            <CardDescription className="text-sm">
              {t('dashboard.charts.distributionByType')}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-2 sm:px-4 pb-4">
            {isLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : analytics?.property_type_breakdown &&
              analytics.property_type_breakdown.length > 0 ? (
              <div className="h-[300px]">
                <PropertyTypeChart data={analytics.property_type_breakdown} />
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                {t('dashboard.noData')}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="xl:col-span-4 bg-card/50 backdrop-blur-sm border-border/50">
          <CardHeader className="px-4 sm:px-6">
            <CardTitle className="text-lg sm:text-xl font-headline text-primary">
              {t('dashboard.recent.topPerformers')}
            </CardTitle>
            <CardDescription className="text-sm">
              {t('dashboard.recent.highestScoring')}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-2 sm:px-4">
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : analytics?.top_performers &&
              analytics.top_performers.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs sm:text-sm">
                        {t('properties.title')}
                      </TableHead>
                      <TableHead className="text-center text-xs sm:text-sm">
                        {t('assessments.score')}
                      </TableHead>
                      <TableHead className="text-center text-xs sm:text-sm">
                        {t('status')}
                      </TableHead>
                      <TableHead className="text-right text-xs sm:text-sm w-16">
                        {t('view')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.top_performers.map(prop => (
                      <TableRow key={prop.id}>
                        <TableCell className="py-3">
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate">
                              {prop.name}
                            </div>
                            <div className="text-xs text-muted-foreground capitalize">
                              {prop.type} - {prop.stars}★
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center text-sm py-3">
                          <span className="font-semibold text-green-600">
                            {prop.score}%
                          </span>
                        </TableCell>
                        <TableCell className="text-center py-3">
                          <Badge
                            variant={
                              prop.is_active ? 'secondary' : 'destructive'
                            }
                            className="text-xs px-2 py-1"
                          >
                            {prop.is_active
                              ? t('properties.active')
                              : t('properties.expired')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right py-3">
                          <Button
                            asChild
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                          >
                            <Link to={`/properties/${prop.id}`}>
                              <ArrowUpRight className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                {t('dashboard.noData')}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
